import dayjs from 'dayjs'

// 与电压一致性、温度热管理等图表保持一致的曲线配色
const chartColors = ['#4960FF', '#0CD5FF', '#2DFFAA', '#FFA441', '#944DFF', '#EE3CD3', '#FF532D', '#0DE24A']
const lineColor = "#3e4961"
const textColor = "#8b93aa"

/** 由曲线颜色生成自上而下渐隐的区域填充，保证填充与线条同色系 */
function areaGradient(color) {
  return {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color },
      { offset: 1, color: 'rgba(255, 255, 255, 0)' }
    ],
    global: false
  }
}

const defaultMin = dayjs().format('YYYY-MM-DD 00:00:00')
const defaultMax = dayjs().format('YYYY-MM-DD 23:59:59')

export function getChartOption(chartData, min = defaultMin, max = defaultMax, meterModel, isShowMmdLine = false) {
  return {
    color: chartColors,
    xAxis: [
      {
        type: 'time',
        min: min,
        max: max,
        nameTextStyle: {
          color: textColor,
        },
        axisLabel: {
          color: textColor,
        },
        boundaryGap: false,
        axisLine: {
          show: true,
          lineStyle: {
            color: lineColor,
            type: 'dashed'
          }
        },
        axisTick: {
          show: true
        },
        nameTextStyle: {
          color: textColor,
        },
      },
    ],
    yAxis: [
      {
        name: '功率(kW)',
        type: 'value',
        nameLocation: 'end',
        nameTextStyle: {
          align: 'center',
          color: textColor,
          padding: [0, 0, 0, 35]
        },
        axisLabel: {
          formatter: function (value) {
            return Math.round(value).toString()
          },
          color: textColor,
        },
        splitLine: {
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        },
        axisLine: {
          show: false
        },
        splitNumber: 10
      },
    ],
    grid: [
      {
        top: '8%',
        left: '1%',
        right: '1%',
        bottom: '18%',
        containLabel: true
      },
    ],
    series: getSeries(...arguments),
    axisPointer: {
      link: [
        {
          xAxisIndex: 'all'
        }
      ],
      label: {
        backgroundColor: '#777'
      }
    },
    tooltip: createTooltip(),
    legend: getLegend(...arguments),
    dataZoom: [
      {
        height: 12,
        bottom: 8,
        textStyle: {
          color: '#8392A5'
        },
        handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '60%',
        dataBackground: {
          areaStyle: {
            color: '#8392A5'
          },
          lineStyle: {
            opacity: 0.8,
            color: '#8392A5'
          }
        },
        brushSelect: true,
      },
      {
        type: 'inside',
      }
    ],
    animation: false,
  }
}

function getSeries(chartData, min, max, meterModel) {
  if (meterModel == 0) {
    return getSeriesForVirtual(chartData, min);
  }

  if (meterModel == 1) {
    return getSeriesForEntity(chartData, min, max);
  }

  return [];
}

function getSeriesForVirtual(chartData, min) {
  const gccm = chartData.gccmChartData ?? [];
  const gwm = chartData.gwmChartData ?? [];
  const pload = chartData.ploadChartData ?? [];
  const demand = chartData.demand;
  const maxDemandInfo = chartData.maxDemandInfo;
  const demandTracks = chartData.demandTracksChartData ?? [];
  const showDemandProtection = chartData.showDemandProtection === true; // 由分组策略决定

  const series = [
    {
      name: '并网点功率',
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1.5
      },
      areaStyle: {
        opacity: 0.3,
        color: areaGradient(chartColors[0])
      },
      data: gccm,
    },
    {
      name: '关口表功率',
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1.5
      },
      areaStyle: {
        opacity: 0.3,
        color: areaGradient(chartColors[1])
      },
      data: gwm,
    },
    {
      name: '负载功率',
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1.5
      },
      areaStyle: {
        opacity: 0.3,
        color: areaGradient(chartColors[2])
      },
      data: pload,
    },
  ];

  // 不存在分组策略时才展示【需量保护阈值】曲线
  if (showDemandProtection) {
    series.push({
      name: '需量保护阈值',
      type: 'line',
      data: [[min, demand]],
      color: 'red',
      markLine: {
        data: [
          {
            name: '需量保护阈值',
            yAxis: demand,
          },
        ],
        lineStyle: {
          normal: {
            type: 'solid',
            color: 'red',
          },
          zIndex: 100,
        },
        label: {
          position: 'insideEndTop',
          formatter(params) {
            return `${params.name}: ${params.value}`
          },
          color: 'red'
        },
      },
    });
  }

  series.push({
    name: maxDemandInfo.name,
    type: 'line',
    data: [[min, maxDemandInfo.value]],
    color: 'red',
    markLine: {
      data: [
        {
          name: `${maxDemandInfo.month}最大需量`,
          yAxis: maxDemandInfo.value,
        },
      ],
      lineStyle: {
        normal: {
          type: 'solid',
          color: 'red',
        },
        zIndex: 100,
      },
      label: {
        position: 'insideEndTop',
        formatter(params) {
          return `${params.name}: ${params.value}`
        },
        color: 'red'
      },
    },
  });

  demandTracks.forEach(track => {
    const serieItem = {
      name: track.name,
      type: 'line',
      showSymbol: false,
      lineStyle: {
        width: 1,
        color: '#FF00E3',
      },
      areaStyle: {
        opacity: 0.1
      },
      data: track.chartData,
      color: '#FF00E3',
    }
    series.push(serieItem);
  });

  return series;
}

function getSeriesForEntity(chartData, min, max) {
  const series = [];
  const entityMeters = chartData.entityMeters ?? [];
  const virtualMeters = chartData.virtualMeters ?? [];
  const datumLines = chartData.datumLines ?? [];
  const allDemands = chartData.allDemands ?? [];
  const demandTracks = chartData.demandTracks ?? [];


  entityMeters.forEach(it => {
    const lineSeries = {
      name: it.name,
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1.5
      },
      areaStyle: {
        opacity: 0.1
      },
      data: it.data,
    }
    series.push(lineSeries);
  })

  virtualMeters.forEach(it => {
    const lineSeries = {
      name: it.name,
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1.5
      },
      areaStyle: {
        opacity: 0.1
      },
      data: it.data,
    }
    series.push(lineSeries);
  })

  datumLines.forEach((it, index) => {
    const demandSeries = getMarkLineSeriesOption({
      name: it.name,
      min: min,
      max: max,
      demand: it.data,
      color: '#0fd',
      labelIndex: index
    })
    series.push(demandSeries)
  })

  allDemands.forEach((it, index) => {
    const demandSeries = getMarkLineSeriesOption({
      name: it.name,
      min: min,
      max: max,
      demand: it.demand,
      // 与基准线的标签一起参与错位，避免所有标签挤在右上角重叠
      labelIndex: datumLines.length + index
    })
    series.push(demandSeries)
  })

  demandTracks.forEach(it => {
    const lineSeries = {
      name: it.name,
      type: 'line',
      showSymbol: false,
      lineStyle: {
        width: 1,
        color: '#FF00E3',
      },
      areaStyle: {
        opacity: 0.1
      },
      data: it.data,
      color: '#FF00E3',
    }
    series.push(lineSeries);
  })

  return series;
}

// 标注线标签的候选位置，多条标注线按顺序轮换，避免全部堆在右上角
const MARK_LINE_LABEL_POSITIONS = ['insideEndTop', 'insideMiddleTop', 'insideStartTop']

function getMarkLineSeriesOption({ name, min, max, demand, color = 'red', labelIndex = 0 }) {
  demand = demand || 0
  // 为 markLine 添加开始和结束两个数据点，使 dataZoom 能正常工作
  // 使用相同的 demand 值，这样不会影响 markLine 的显示
  const data = max ? [[min, demand], [max, demand]] : [[min, demand]];

  return {
    name: name,
    type: 'line',
    data: data,
    color,
    markLine: {
      symbol: 'none',
      data: [
        {
          name: name,
          yAxis: demand,
        },
      ],
      lineStyle: {
        normal: {
          type: 'solid',
          color,
          width: 1,
        },
        zIndex: 100,
      },
      label: {
        position: MARK_LINE_LABEL_POSITIONS[labelIndex % MARK_LINE_LABEL_POSITIONS.length],
        // 同一位置的标签再按轮次向上叠放一层，进一步降低重叠概率
        distance: [0, Math.floor(labelIndex / MARK_LINE_LABEL_POSITIONS.length) * 16],
        formatter(params) {
          return `${params.name}: ${params.value}`
        },
        color
      },
    },
  }
}

function getLegend(chartData, min, max, meterModel, isShowMmdLine) {
  if (meterModel == 0) {
    return getLegedForVirtual(chartData, isShowMmdLine);
  }

  if (meterModel == 1) {
    return getLegedForEntity(chartData);
  }

  return null;
}

function getLegedForVirtual(chartData, isShowMmdLine) {
  const demandTracks = chartData.demandTracksChartData ?? [];
  const legend = {
    show: true,
    icon: 'circle',
    bottom: 40,
    itemGap: 20,
    textStyle: {
      color: textColor
    },
    selected: {}
  };
  demandTracks.forEach(track => {
    legend.selected[track.name] = false;
  })
  legend.selected[chartData.maxDemandInfo.name] = isShowMmdLine;

  return legend;
}

function getLegedForEntity(chartData) {
  const legend = {
    show: false,
    icon: 'circle',
    bottom: 40,
    itemGap: 20,
    textStyle: {
      color: textColor
    },
    selected: {}
  };
  Object.values(chartData).forEach(group => {
    group.forEach(it => {
      legend.selected[it.name] = it.isShow;
    })
  })
  return legend;
}

function createTooltip(config = {}) {
  const $str = window.$str;

  const timeFormatter = (value) => {
    // 格式化时间：如果是时间戳，转换为年月日时分秒；如果是时间字符串，直接使用
    let timeValue = value;
    if (typeof timeValue === 'number' || (typeof timeValue === 'string' && /^\d+$/.test(timeValue))) {
      // 判断是否是时间戳（毫秒或秒）
      const timestamp = Number(timeValue);
      const isMillisecond = timestamp > 946684800000; // 2000-01-01的时间戳（毫秒）
      timeValue = dayjs(isMillisecond ? timestamp : timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
    } else if (timeValue instanceof Date) {
      timeValue = dayjs(timeValue).format('YYYY-MM-DD HH:mm:ss');
    }
    return timeValue;
  };

  return {
    trigger: 'axis',
    axisPointer: {
      type: 'cross'
    },
    className: 'chart-tooltip',
    backgroundColor: 'rgba(255, 255, 255, 0.90)',
    borderWidth: 0,
    textStyle: {
      color: '#6A727C'
    },
    formatter: (params) => {
      let time = params[0].axisValue;
      time = timeFormatter(time);

      const str = (list) => {
        let t = '';
        for (const item of list) {
          const value = Array.isArray(item.data) ? item.data[1] : item.data;
          t += /* html */`
            <div class="chart-tooltip__series" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; line-height: 1.5;">
              <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                <div class="chart-tooltip__point" style="background: ${item.color}; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; display: inline-block; vertical-align: middle;"></div>
                <div class="chart-tooltip__series-name" style="display: inline-block; vertical-align: middle;">${item.seriesName}</div>
              </div>
              <div class="chart-tooltip__value" style="flex-shrink: 0; display: inline-block; vertical-align: middle; text-align: right;">${value ?? '--'}</div>
            </div>`;
        }
        return t;
      };

      return /* html */`
        <div class="chart-tooltip__time" style="margin-bottom: 12px;">${time}</div>
        <div class="chart-tooltip__items-container">${str(params)}</div>        
      `;
    }
  }
}
